import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { typeColors, typeLabels, TypeVariant } from './types';
import { Colors } from '../../../../themes/colors';
import { TypeIcon } from './TypeIcon';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

/**
 * Chevron Down icon (8px) for pill components
 */
const ChevronDown: React.FC<{ color?: string }> = ({
  color = Colors.role.textOnBrand,
}) => (
  <Svg width={8} height={8} viewBox="0 0 8 8" fill="none">
    <Path
      d="M1 2.5L4 5.5L7 2.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export interface TypePillProps {
  /**
   * The type variant that determines the color and label
   */
  variant: TypeVariant;

  /**
   * Optional custom label text (overrides default)
   */
  label?: string;

  /**
   * Whether to show the type icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Whether to show the chevron indicator
   * @default true
   */
  showChevron?: boolean;

  /**
   * Callback when pressed
   */
  onPress?: () => void;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;
}

/**
 * TypePill - Pill-shaped type badge with icon, label and chevron
 *
 * From Figma Crop Circle System - Types Pill component
 * Node: 1992-453
 *
 * Specs:
 * - Height: 32px
 * - Border radius: 99px (pill)
 * - Padding: 12px horizontal, 4px vertical
 * - Gap: 6px
 * - Font: Inter 400 12px/16px, white
 * - Icon: 16px, white
 */
export const TypePill: React.FC<TypePillProps> = ({
  variant,
  label,
  showIcon = true,
  showChevron = true,
  onPress,
  disabled = false,
  testID,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const backgroundColor = typeColors[variant];
  const displayLabel = label ?? typeLabels[variant];

  const content = (
    <View
      style={[
        styles.container,
        {
          backgroundColor,
          opacity: disabled ? 0.5 : isHovered ? 0.9 : 1,
        },
      ]}
    >
      {showIcon && (
        <TypeIcon variant={variant} size={16} color={Colors.role.textOnBrand} />
      )}
      <Text style={styles.label}>{displayLabel}</Text>
      {showChevron && <ChevronDown />}
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        testID={testID}
        accessibilityRole="button"
        accessibilityState={{ disabled }}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View testID={testID} accessibilityRole="text">
      {content}
    </View>
  );
};

TypePill.displayName = 'TypePill';

const styles = themedStyleSheet((role) => ({
  container: {
    height: 32,
    borderRadius: 99,
    paddingHorizontal: 12,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
  },
  label: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: role.textOnBrand,
  },
}));
